import React, { FunctionComponent, useCallback, useContext, useMemo } from 'react'
import styles from './GalleryComments.module.sass'
import { galleryActions, GalleryData } from '../../../../../redux/global/gallery/gallery-actions'
import DirectionPicker from '../../../../pages/projects/ProjectPage/Chat/DirectionPicker'
import TaskPicker from '../TaskPicker/TaskPicker'
import { isEmpty } from 'fp-ts/Array'
import { statusContext } from '../../../../../constants/contexts'
import { RequestStatus } from '../../../../../responses/response'
import Skeleton from 'react-loading-skeleton'
import UserPanel from '../../../../data-components/elements/UserElement/UserElement'
import CommentElement from '../../../../data-components/elements/CommentElement/CommentElement'
import { getId } from '../../../../../utils/entity-utils'
import { FileData } from '../../../../../responses/files'
import { useRootDispatch, useRootSelector } from '../../../../../utils/hooks'
import { pick } from '../../../../../utils/utils'
import { identity, pipe } from 'fp-ts/es6/function'
import { SortDirection } from '../../../../../constants/direction'
import { reverse } from 'fp-ts/es6/Array'
import { directionSelector, taskSelector } from '../../../../../utils/selectors'
import clsx from 'clsx'
import Spacer from '../../../Spacer'
import Color from '../../../../data-components/pickers/ColorPicker/Color'
import Selector from '../../../Selector'
import { userDescription } from '../../../../../utils/lenses'
import { processDate } from '../../../../../utils/date-utils'
import { EMPTY_USER } from '../../../../../responses/users'

interface Props {
    className?: string,
    data: GalleryData,
}

const GalleryComments: FunctionComponent<Props> = props => {
    const dispatch = useRootDispatch()
    const {data, comments} = useRootSelector(state => pick(state.global.gallery, 'data', 'comments'))
    const direction = useRootSelector(directionSelector)
    const status = useContext(statusContext)

    const sortedComments = useMemo(() => pipe(
        comments,
        direction === SortDirection.DESC ? identity : reverse,
    ), [comments, direction])

    const setCurrent = useCallback((file: FileData) => dispatch(galleryActions.setCurrent(file)), [dispatch])

    return <div className={clsx(props.className, styles.comments, styles[data.type])}>
        {
            data.type !== 'none' && <div className={styles.header}>
                <DirectionPicker/>
                {
                    (data.type === 'task' || data.type === 'comment') && <>
                        <div className={styles.title}>Выбрано: </div>
                        <TaskPicker
                            objectId={data.objectId}
                            tasksId={data.tasksId}
                            onChange={tasks => dispatch(galleryActions.open({
                                ...data,
                                tasksId: tasks.map(getId),
                                current: null,
                            }))}
                        />
                    </>
                }
            </div>
        }
        <div className={styles.list}>
            {
                (isEmpty(sortedComments) && status === RequestStatus.FETCHING) &&
                <Skeleton height={100} count={8}/>
            }
            {
                sortedComments.map(comment => <div key={comment.id} className={styles.comment}>
                    <div className={styles.title}>
                        <UserPanel user={userDescription.set(processDate(comment.created_at))(comment.user ?? EMPTY_USER)}/>
                        <Spacer/>
                        {
                            comment.owner_type === 'task' && <Selector
                                selector={taskSelector(comment.owner_id)}
                                render={value => <Color className={styles.color} color={value?.type.color}>{value?.type.short_name}</Color>}
                            />
                        }
                    </div>
                    <CommentElement
                        openAlbum
                        comment={comment}
                        editable={props.data.type !== 'project' && props.data.type !== 'object'}
                        onFileClick={setCurrent}
                    />
                </div>)
            }
        </div>
    </div>
}

export default GalleryComments